import { useState, useEffect } from 'react';
import { TrophyIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';

interface PrizeEntry {
  id: string;
  position_label: string;
  category: string;
  prize_amount: number;
  currency: string;
  description?: string;
  display_order: number;
}

interface Tournament {
  id: string;
  name: string;
  start_date: string;
}

interface Award {
  id: string;
  name: string;
  description?: string;
}

function formatCurrency(amount: number, currency = '₦') {
  return `${currency}${amount.toLocaleString()}`;
}

export default function MobilePrizeBreakdown() {
  const [prizes, setPrizes] = useState<PrizeEntry[]>([]);
  const [awards, setAwards] = useState<Award[]>([]);
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedId, setSelectedId] = useState('');
  const [totalPrizePool, setTotalPrizePool] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'prizes' | 'awards'>('prizes');

  useEffect(() => {
    fetchTournaments();
    fetchAwards();
  }, []);

  useEffect(() => {
    if (selectedId) fetchPrizes();
  }, [selectedId]);

  const fetchTournaments = async () => {
    try {
      const { data } = await supabase
        .from('tournaments')
        .select('id, name, start_date')
        .in('status', ['upcoming', 'ongoing', 'completed'])
        .order('start_date', { ascending: false });

      if (data?.length) {
        setTournaments(data);
        setSelectedId(data[0].id);
      }
    } catch {}
  };

  const fetchPrizes = async () => {
    try {
      setLoading(true);
      const { data } = await supabase
        .from('prize_breakdown')
        .select('*')
        .eq('tournament_id', selectedId)
        .order('display_order', { ascending: true });

      if (data) {
        setPrizes(data);
        setTotalPrizePool(data.reduce((sum, p) => sum + Number(p.prize_amount), 0));
      }
    } catch {
      setPrizes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAwards = async () => {
    try {
      const { data } = await supabase
        .from('award_categories')
        .select('*')
        .order('name', { ascending: true });
      if (data) setAwards(data);
    } catch {}
  };

  const prizesByCategory = prizes.reduce((acc, prize) => {
    if (!acc[prize.category]) acc[prize.category] = [];
    acc[prize.category].push(prize);
    return acc;
  }, {} as Record<string, PrizeEntry[]>);

  const positionStyle = (index: number) => {
    if (index === 0) return 'bg-amber-50 border-amber-200 text-amber-700';
    if (index === 1) return 'bg-gray-50 border-gray-200 text-gray-600';
    if (index === 2) return 'bg-orange-50 border-orange-200 text-orange-600';
    return 'bg-blue-50 border-blue-200 text-blue-600';
  };

  return (
    <div className="pb-10">
      {/* Header */}
      <div className="bg-gradient-to-r from-golf-green to-golf-orange px-4 pt-4 pb-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
            <TrophyIcon className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-white font-display font-bold text-xl">Prize Breakdown</h1>
        </div>

        {totalPrizePool > 0 && (
          <div className="bg-white/15 rounded-xl p-3 flex items-center gap-3">
            <CurrencyDollarIcon className="w-5 h-5 text-white flex-shrink-0" />
            <div>
              <p className="text-white/80 text-xs">Total Prize Pool</p>
              <p className="text-white font-display font-bold text-xl">
                {formatCurrency(totalPrizePool, prizes[0]?.currency)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Tournament Selector */}
      {tournaments.length > 1 && (
        <div className="px-4 py-3 bg-white border-b border-gray-100">
          <select
            value={selectedId}
            onChange={(e) => setSelectedId(e.target.value)}
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-golf-orange bg-gray-50 text-gray-700"
          >
            {tournaments.map(t => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-100 bg-white sticky top-14 z-10">
        {(['prizes', 'awards'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-xs font-semibold transition-colors border-b-2 capitalize ${
              activeTab === tab ? 'text-golf-orange border-golf-orange' : 'text-gray-400 border-transparent'
            }`}
          >
            {tab === 'prizes' ? 'Prize Money' : 'Awards'}
          </button>
        ))}
      </div>

      {/* Prizes Tab */}
      {activeTab === 'prizes' && (
        <div className="px-4 pt-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-golf-orange border-t-transparent" />
            </div>
          ) : prizes.length === 0 ? (
            <div className="text-center py-16">
              <TrophyIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No prize breakdown available yet.</p>
              <p className="text-gray-300 text-xs mt-1">Details will be announced soon.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {Object.entries(prizesByCategory).map(([category, categoryPrizes]) => (
                <div key={category} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-100">
                    <h2 className="font-semibold text-golf-gray text-sm">{category}</h2>
                  </div>
                  <div className="divide-y divide-gray-50">
                    {categoryPrizes.map((prize, idx) => (
                      <div key={prize.id} className={`px-4 py-3 flex items-center gap-3 ${idx === 0 ? 'bg-amber-50/40' : ''}`}>
                        <div className={`w-9 h-9 rounded-full border flex items-center justify-center flex-shrink-0 text-xs font-bold ${positionStyle(idx)}`}>
                          {idx === 0 ? <TrophyIcon className="w-4 h-4" /> : idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-800 truncate">{prize.position_label}</p>
                          {prize.description && (
                            <p className="text-xs text-gray-400 mt-0.5 truncate">{prize.description}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-golf-orange font-bold text-base">
                            {formatCurrency(prize.prize_amount, prize.currency)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}

              <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
                <h3 className="font-semibold text-gray-700 text-sm mb-2">Prize Notes</h3>
                <ul className="space-y-1.5 text-xs text-gray-500">
                  <li>• Prizes are subject to applicable taxes and regulations</li>
                  <li>• Winners must be present to claim their prizes</li>
                  <li>• All decisions by the tournament committee are final</li>
                  <li>• Amounts in Nigerian Naira (₦) unless otherwise specified</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Awards Tab */}
      {activeTab === 'awards' && (
        <div className="px-4 pt-4">
          {awards.length === 0 ? (
            <div className="text-center py-16">
              <TrophyIcon className="w-12 h-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400 text-sm">No awards available.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {awards.map((award) => (
                <div key={award.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center flex-shrink-0">
                    <TrophyIcon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-golf-gray text-sm">{award.name}</p>
                    {award.description && (
                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{award.description}</p>
                    )}
                  </div>
                </div>
              ))}

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 mt-2">
                <p className="text-xs text-blue-700 leading-relaxed">
                  <span className="font-semibold">Note:</span> Award winners will be announced at the prize ceremony. Winners are selected based on performance, sportsmanship, and tournament committee decisions.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
